import { createContext, useContext, useEffect, useState } from "react";
import { track } from "@/lib/track";

const CartContext = createContext(null);
const STORAGE_KEY = "pm_cart";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  // variant = { name, sku, price } | null
  const addItem = (product, qty = 1, variant = null) => {
    const cartId = variant?.sku ? `${product.id}::${variant.sku}` : product.id;
    const rawPrice = variant && variant.price ? Number(variant.price) : Number(product.price);
    const discount = Number(product.discount) || 0;
    const finalPrice = discount > 0 ? Math.round(rawPrice * (1 - discount / 100)) : Math.round(rawPrice);
    const sku = variant?.sku || product.sku || "";
    track("add_to_cart", { product_id: product.id, product_name: product.name, category: product.category, pet_type: product.pet_type, value: finalPrice * qty });
    setItems((prev) => {
      const found = prev.find((i) => i.id === cartId);
      if (found) {
        return prev.map((i) => (i.id === cartId ? { ...i, qty: i.qty + qty } : i));
      }
      return [...prev, {
        id: cartId,
        productId: product.id,
        name: product.name,
        variant: variant?.name || "",
        sku,
        price: finalPrice,
        original_price: Math.round(rawPrice),
        discount,
        currency: product.currency,
        image_url: product.image_url,
        presentation: product.presentation,
        qty,
      }];
    });
  };

  const setQty = (id, qty) => {
    setItems((prev) =>
      qty <= 0 ? prev.filter((i) => i.id !== id) : prev.map((i) => (i.id === id ? { ...i, qty } : i))
    );
  };

  const removeItem = (id) => setItems((prev) => prev.filter((i) => i.id !== id));
  const clear = () => setItems([]);

  const count = items.reduce((s, i) => s + i.qty, 0);
  const total = items.reduce((s, i) => s + i.qty * Number(i.price || 0), 0);

  return (
    <CartContext.Provider value={{ items, addItem, setQty, removeItem, clear, count, total, open, setOpen }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
